import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useAuth, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { BASE, URLS, APIS, SF_MY_INFO_CHG_INFO } from "@/tra/tancis/ptli/acrdt/lgn/services/PtliPwdChgService";

/*
 * Change Password
 * !== 비밀번호 변경 ==!
 */
export const PTLI0204001S = (props: any) => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { usrDmnId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "PTL0003" }, { area: "cstmCd" }, { area: "orgzSub" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // My information modification form !== 나의정보 수정 폼 ==!
        myInfoChgInfo: useForm({
            defaultSchema: SF_MY_INFO_CHG_INFO,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Search user registration application information based on login ID !== 로그인ID 기준 사용자 정보 조회==!
        getMyInfo: useFetch({
            api: (usrDmnId) => APIS.getMyInfo(usrDmnId),
            enabled: false,
            onSuccess: (data) => {
                if (!comnUtils.isEmpty(data.myInfo.content)) {
                    // Initial data setting (to check changes) !== 초기 데이터 세팅 (변경사항 체크용) ==!
                    const initMyInfoData = data.myInfo.content;

                    form.myInfoChgInfo.reset();

                    form.myInfoChgInfo.setValues(initMyInfoData);
                }
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    const { errorMessage } = error.response.data;

                    modal.openModal({
                        content: errorMessage.content,
                        onConfirm: () => {
                            navigate("/");
                        },
                        close: false,
                    });

                    return;
                }

                toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),

        // Update User Information !== 사용자 정보 수정 ==!
        updateMyInfo: useFetch({
            api: (data) => APIS.saveMyInfo({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        if (!comnUtils.isEmpty(auth.get("usrId"))) {
                            fetch.getMyInfo.fetch(auth.get("usrId"));
                        }
                    },
                    close: false,
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.myInfoChgInfo.setErrors(error.response.data.errors);
                    return;
                }

                const { errorCode, errorMessage } = error.response.data;

                if (errorCode === "-999") {
                    modal.openModal({
                        content: errorMessage, // Passwords do not match !== 비밀번호가 일치하지 않습니다. ==!
                    });

                    form.myInfoChgInfo.setFocus("usrPwdCfrm");
                    return;
                }

                toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        checkPwd: () => {
            const usrPwd = form.myInfoChgInfo.getValue("usrPwd");
            const usrPwdCfrm = form.myInfoChgInfo.getValue("usrPwdCfrm");

            if (!comnUtils.isEmpty(usrPwd) && !comnUtils.equals(usrPwd, usrPwdCfrm)) {
                form.myInfoChgInfo.setError("usrPwdCfrm", {
                    message: "msg.ptl.00712", // Passwords do not match. !== 비밀번호가 일치하지 않습니다. ==!
                    type: "error",
                });
            } else {
                form.myInfoChgInfo.clearErrors("usrPwdCfrm");
            }
        },
        // Update User Information !== 사용자 정보 수정 ==!
        onSaveBtnClick: () => {
            if (!comnUtils.isEmpty(form.myInfoChgInfo.errors)) {
                const data = form.myInfoChgInfo.getValues();

                if (!comnUtils.isEmpty(data.usrPwd) || !comnUtils.isEmpty(data.usrPwd)) {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                    return;
                }
            }

            form.myInfoChgInfo.handleSubmit(
                () => {
                    const data = form.myInfoChgInfo.getValues();
                    const usrPwd = data.usrPwd;
                    const usrPwdCfrm = data.usrPwdCfrm;

                    // Check for missing password !== 비밀번호 누락 여부 체크 ==!
                    if (!comnUtils.isEmpty(usrPwd) && comnUtils.isEmpty(usrPwdCfrm)) {
                        toast.showToast({ type: "warning", content: "msg.00002" });
                        form.myInfoChgInfo.setFocus("usrPwdCfrm");
                        return;
                    }

                    if (comnUtils.isEmpty(usrPwd) && !comnUtils.isEmpty(usrPwdCfrm)) {
                        toast.showToast({ type: "warning", content: "msg.00002" });
                        form.myInfoChgInfo.setFocus("usrPwd");
                        return;
                    }

                    if (typeof data.fileGrpUuid === "object") {
                        data.fileSaveDto = data.fileGrpUuid;
                        data.fileGrpUuid = null;
                    }

                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.updateMyInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
    };

    useEffect(() => {
        if (!comnUtils.isEmpty(auth.get("usrId"))) {
            fetch.getMyInfo.fetch(auth.get("usrId"));
        }
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.myInfoChgInfo.schema.usrDmnId} readOnly={true}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.myInfoChgInfo.schema.curPwdCfrm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.myInfoChgInfo.schema.usrPwd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.myInfoChgInfo.schema.usrPwdCfrm}
                                    onBlur={() => {
                                        handler.checkPwd();
                                    }}
                                />
                            </Group.Row>
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Right>
                                <Button
                                    role="save"
                                    onClick={() => {
                                        handler.onSaveBtnClick();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>
        </Page>
    );
};
